import { useEffect, useState } from 'react';

type VisibilityState = { visible: boolean; focused: boolean };

// Estado compartilhado (singleton) para evitar múltiplos listeners/setState
const sharedState: VisibilityState = {
  visible: typeof document !== 'undefined' ? !document.hidden : true,
  focused: typeof document !== 'undefined' ? document.hasFocus() : true,
};

const subscribers = new Set<() => void>();
let listenersAttached = false;

const notify = () => {
  subscribers.forEach((cb) => cb());
};

const attachListeners = () => {
  if (listenersAttached || typeof document === 'undefined') return;
  listenersAttached = true;

  const handleChange = () => {
    // Quando a aba fica visível novamente, assumimos foco verdadeiro para manter a página ativa
    const visible = !document.hidden;
    const focused = visible ? true : document.hasFocus();
    const nextState: VisibilityState = { visible, focused };

    // Atualiza somente se houver mudança real
    if (
      nextState.visible === sharedState.visible &&
      nextState.focused === sharedState.focused
    ) {
      return;
    }

    sharedState.visible = nextState.visible;
    sharedState.focused = nextState.focused;
    notify();
  };

    document.addEventListener('visibilitychange', handleChange);
    window.addEventListener('focus', () => {
      sharedState.visible = true;
      sharedState.focused = true;
      notify();
    });
  window.addEventListener('blur', handleChange);
};

const shouldDebugVisibility = typeof window !== 'undefined' && !!(window as any).__DEBUG_VISIBILITY__;

export function usePageVisibility() {
  const [state, setState] = useState<VisibilityState>({ ...sharedState });

  useEffect(() => {
    attachListeners();
    const cb = () => setState({ ...sharedState });
    subscribers.add(cb);
    if (shouldDebugVisibility) {
      console.info('[usePageVisibility] subscribe', {
        visible: sharedState.visible,
        focused: sharedState.focused,
        timestamp: new Date().toISOString(),
      });
    }
    return () => {
      subscribers.delete(cb);
      if (shouldDebugVisibility) {
        console.info('[usePageVisibility] unsubscribe', {
          timestamp: new Date().toISOString(),
        });
      }
    };
  }, []);

  return {
    isVisible: state.visible,
    isFocused: state.focused,
    isPageActive: state.visible && state.focused,
  };
}